import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Layers,
  X,
  Pencil,
  Trash2,
  Check,
  Loader2,
  Plus,
  FileText,
} from "lucide-react";

/**
 * DocumentGroupsPanel.
 *
 * Renders above the document list. Shows existing groups as colour-coded
 * chips with a member count, lets the user rename or delete a group, and
 * surfaces the documents inside via an expand panel. Group creation is
 * driven by DocumentsTab's selection toolbar (it calls the exported
 * `createGroupFromSelection` helper / opens the inline create flow).
 *
 * Backend endpoints used:
 *   GET    /api/cases/{caseId}/groups
 *   PATCH  /api/cases/{caseId}/groups/{groupId}
 *   DELETE /api/cases/{caseId}/groups/{groupId}
 */
/**
 * Color mapping used across the tag / group UI. Exported so document cards
 * and the tag filter bar can render matching chips without duplicating the
 * palette. Keep in sync with backend/models.py GROUP_COLOR Literal.
 */
export const TAG_COLOR_STYLES = {
  teal:   { bar: "border-l-[#1B6C62]", bg: "bg-[#E6F5F2]",  text: "text-[#0D2B52]", pill: "bg-[#1B6C62] text-white", dot: "bg-[#1B6C62]", label: "Teal" },
  navy:   { bar: "border-l-[#0D2B52]", bg: "bg-[#E3E8F0]",  text: "text-[#0D2B52]", pill: "bg-[#0D2B52] text-white", dot: "bg-[#0D2B52]", label: "Navy" },
  gold:   { bar: "border-l-[#C9A961]", bg: "bg-[#FAF6E8]",  text: "text-[#8A6D3B]", pill: "bg-[#C9A961] text-white", dot: "bg-[#C9A961]", label: "Gold" },
  rose:   { bar: "border-l-[#B35D4C]", bg: "bg-[#F8E9E5]",  text: "text-[#8A4537]", pill: "bg-[#B35D4C] text-white", dot: "bg-[#B35D4C]", label: "Rose" },
  slate:  { bar: "border-l-[#6B7280]", bg: "bg-[#EEEFF1]",  text: "text-[#1A1A1A]", pill: "bg-[#6B7280] text-white", dot: "bg-[#6B7280]", label: "Slate" },
  purple: { bar: "border-l-[#7C3AED]", bg: "bg-[#EDE4FB]",  text: "text-[#4C1D95]", pill: "bg-[#7C3AED] text-white", dot: "bg-[#7C3AED]", label: "Purple" },
  green:  { bar: "border-l-[#15803D]", bg: "bg-[#DCFCE7]",  text: "text-[#14532D]", pill: "bg-[#15803D] text-white", dot: "bg-[#15803D]", label: "Green" },
  red:    { bar: "border-l-[#B91C1C]", bg: "bg-[#FEE2E2]",  text: "text-[#7F1D1D]", pill: "bg-[#B91C1C] text-white", dot: "bg-[#B91C1C]", label: "Red" },
  orange: { bar: "border-l-[#C2410C]", bg: "bg-[#FFEDD5]",  text: "text-[#7C2D12]", pill: "bg-[#C2410C] text-white", dot: "bg-[#C2410C]", label: "Orange" },
  cyan:   { bar: "border-l-[#0891B2]", bg: "bg-[#CFFAFE]",  text: "text-[#164E63]", pill: "bg-[#0891B2] text-white", dot: "bg-[#0891B2]", label: "Cyan" },
};
export const TAG_COLOR_KEYS = ["teal", "navy", "gold", "rose", "slate", "purple", "green", "red", "orange", "cyan"];
// Back-compat alias so existing internal references keep working.
const COLOR_STYLES = TAG_COLOR_STYLES;

export default function DocumentGroupsPanel({ caseId, docs, refreshKey, onGroupsChange }) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cases/${caseId}/groups`);
      setGroups(data || []);
      onGroupsChange?.(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load groups.");
    } finally {
      setLoading(false);
    }
  };

  // Reload when refreshKey changes (DocumentsTab bumps this after creating
  // a new group from the selection toolbar).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [caseId, refreshKey]);

  const docById = Object.fromEntries((docs || []).map((d) => [d.id, d]));

  const handleDelete = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? Documents stay in the vault.`)) return;
    try {
      await api.delete(`/cases/${caseId}/groups/${g.id}`);
      toast.success("Group deleted.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't delete group.");
    }
  };

  const handleRename = async (g) => {
    const name = (renameValue || "").trim();
    if (!name) {
      toast.error("Name can't be empty.");
      return;
    }
    if (name === g.name) {
      setRenamingId(null);
      return;
    }
    try {
      await api.patch(`/cases/${caseId}/groups/${g.id}`, { name });
      toast.success("Renamed.");
      setRenamingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't rename.");
    }
  };

  const handleRemoveDoc = async (g, docId) => {
    const next = (g.doc_ids || []).filter((id) => id !== docId);
    try {
      await api.patch(`/cases/${caseId}/groups/${g.id}`, { doc_ids: next });
      toast.success("Removed from group.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update group.");
    }
  };

  if (loading) {
    return (
      <div className="card" data-testid="doc-groups-panel">
        <div className="text-sm text-[#6B7280] italic flex items-center gap-2">
          <Loader2 size={14} strokeWidth={1.75} className="animate-spin" /> Loading groups…
        </div>
      </div>
    );
  }

  if (!groups.length) return null;

  return (
    <div className="card" data-testid="doc-groups-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Layers size={16} strokeWidth={1.75} className="text-[#1B6C62]" />
          <h3 className="font-serif text-lg text-[#0D2B52]">Document groups</h3>
        </div>
        <span className="font-mono text-[11px] text-[#6B7280]">{groups.length} {groups.length === 1 ? "group" : "groups"}</span>
      </div>

      <div className="space-y-2.5" data-testid="doc-groups-list">
        {groups.map((g) => {
          const c = COLOR_STYLES[g.color] || COLOR_STYLES.teal;
          const expanded = expandedId === g.id;
          const renaming = renamingId === g.id;
          const members = (g.doc_ids || []).map((id) => docById[id]).filter(Boolean);
          return (
            <div
              key={g.id}
              className={`border-l-4 ${c.bar} ${c.bg} p-3`}
              data-testid={`doc-group-${g.id}`}
            >
              <div className="flex items-center justify-between gap-3">
                <button
                  onClick={() => setExpandedId(expanded ? null : g.id)}
                  className="flex items-center gap-2.5 flex-1 min-w-0 text-left"
                  data-testid={`doc-group-${g.id}-toggle`}
                >
                  {renaming ? (
                    <input
                      autoFocus
                      value={renameValue}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); handleRename(g); }
                        if (e.key === "Escape") { setRenamingId(null); }
                      }}
                      className="flex-1 bg-white border border-[#1B6C62] px-2 py-1 text-sm"
                      data-testid={`doc-group-${g.id}-rename-input`}
                    />
                  ) : (
                    <>
                      <span className={`font-serif text-[15px] ${c.text} truncate`}>{g.name}</span>
                      <span className={`font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${c.pill}`}>
                        {(g.doc_ids || []).length} doc{(g.doc_ids || []).length === 1 ? "" : "s"}
                      </span>
                    </>
                  )}
                </button>
                <div className="flex items-center gap-1">
                  {renaming ? (
                    <>
                      <button onClick={() => handleRename(g)} className="btn-ghost p-1.5" title="Save name" data-testid={`doc-group-${g.id}-save`}>
                        <Check size={14} strokeWidth={2} />
                      </button>
                      <button onClick={() => setRenamingId(null)} className="btn-ghost p-1.5" title="Cancel">
                        <X size={14} strokeWidth={2} />
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => { setRenamingId(g.id); setRenameValue(g.name); }}
                        className="btn-ghost p-1.5"
                        title="Rename group"
                        data-testid={`doc-group-${g.id}-rename`}
                      >
                        <Pencil size={13} strokeWidth={1.75} />
                      </button>
                      <button
                        onClick={() => handleDelete(g)}
                        className="btn-ghost p-1.5 hover:text-[#B35D4C]"
                        title="Delete group (documents stay in the vault)"
                        data-testid={`doc-group-${g.id}-delete`}
                      >
                        <Trash2 size={13} strokeWidth={1.75} />
                      </button>
                    </>
                  )}
                </div>
              </div>

              {expanded && (
                <div className="mt-3 pt-3 border-t border-white/60 space-y-1.5" data-testid={`doc-group-${g.id}-members`}>
                  {members.length === 0 ? (
                    <p className="text-[12px] text-[#6B7280] italic">No documents in this group yet.</p>
                  ) : (
                    members.map((d) => (
                      <div key={d.id} className="flex items-center justify-between gap-2 bg-white/70 px-2.5 py-1.5 border border-white/80">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText size={12} strokeWidth={1.75} className="text-[#1B6C62] shrink-0" />
                          <span className="text-[12.5px] text-[#1A1A1A] font-serif truncate">
                            {d.title || d.original_name}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRemoveDoc(g, d.id)}
                          className="btn-ghost p-1 text-[#6B7280] hover:text-[#B35D4C]"
                          title="Remove from group"
                          data-testid={`doc-group-${g.id}-remove-${d.id}`}
                        >
                          <X size={12} strokeWidth={2} />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
